import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { salesItemService, SalesItemDTO } from '../../services/salesItemService';
import '../../pages/Products/ProductCategoryListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const SalesItemListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<SalesItemDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const productId = searchParams.get('productId');

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (productId) {
        try {
          response = await salesItemService.getSalesItemsByProduct(parseInt(productId));
          const items = response?.data?.data;
          if (Array.isArray(items)) {
            setItems(items);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Failed to load product-specific sales items:', err);
          // If product-specific items fail, fall through to load all items
        }
      }
      // Load all items if no productId or product-specific call failed
      response = await salesItemService.getSalesItems(1, 100);
      const allItems = response?.data?.data?.data ?? response?.data?.data;
      if (Array.isArray(allItems)) {
        setItems(allItems);
      }
      setLoading(false);
    } catch (err) {
      setError(t('salesItem.failedToLoadItem'));
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      let response;
      if (productId) {
        try {
          response = await salesItemService.getSalesItemsByProduct(parseInt(productId));
          const items = response?.data?.data;
          if (Array.isArray(items)) {
            setItems(items);
            setRefreshing(false);
            return;
          }
        } catch (err) {
          console.error('Failed to load product-specific sales items:', err);
          // If product-specific items fail, fall through to load all items
        }
      }
      // Load all items if no productId or product-specific call failed
      response = await salesItemService.getSalesItems(1, 100);
      const allItems = response?.data?.data?.data ?? response?.data?.data;
      if (Array.isArray(allItems)) {
        setItems(allItems);
      }
    } catch (err) {
      setError(t('salesItem.failedToLoadItem'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'productSKU',
      label: t('salesItem.productSKU'),
      sortable: true,
      width: '130px',
      render: (value) => <code>{value}</code>,
    },
    {
      key: 'productName',
      label: t('salesItem.productName'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'sellingPrice',
      label: t('salesItem.sellingPrice'),
      sortable: true,
      width: '130px',
      render: (value, row) => (
        <strong>{Number(value).toFixed(2)} {row.currency}</strong>
      ),
    },
    {
      key: 'salesChannel',
      label: t('salesItem.salesChannel'),
      sortable: true,
      width: '120px',
      render: (value) => <span className="badge badge-info">{value}</span>,
    },
    {
      key: 'discountAllowed',
      label: t('salesItem.discountAllowed'),
      sortable: true,
      width: '100px',
      hideTablet: true,
      render: (value) => (
        <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`}>
          {value ? t('common.active') : t('salesItem.no')}
        </span>
      ),
    },
    {
      key: 'returnAllowed',
      label: t('salesItem.returnAllowed'),
      sortable: true,
      width: '100px',
      hideMobile: true,
      render: (value) => (
        <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`}>
          {value ? t('salesItem.yes') : t('salesItem.no')}
        </span>
      ),
    },
    {
      key: 'taxName',
      label: t('salesItem.tax'),
      width: '140px',
      hideTablet: true,
      render: (_value, row) =>
        row.taxPercentage != null && row.taxPercentage > 0
          ? `${row.taxPercentage}%`
          : <span style={{ color: 'var(--ims-text-muted)' }}>—</span>,
    },
    {
      key: 'isActive',
      label: t('salesItem.status'),
      width: '100px',
      hideMobile: true,
      render: (value) => (
        <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`}>
          {value ? t('salesItem.active') : t('salesItem.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
       <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/sales-items/${item.salesItemId}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: (
        <SquarePen size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/sales-items/${item.salesItemId}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
    {
      icon: (
        <Trash2 size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/sales-items/${item.salesItemId}/delete`),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    },
  ];

  return (
    <Layout>
      <div className="container">
        {productId && (
          <div className="alert" style={{
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            border: '1px solid #93c5fd',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <span>
              <strong>Filter Active:</strong> Showing sales items for Product ID: {productId}
            </span>
            <button
              onClick={() => navigate('/sales-items')}
              style={{
                background: 'none',
                border: 'none',
                color: '#1e40af',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
                padding: '0 8px'
              }}
              title="Clear filter"
            >
              ✕
            </button>
          </div>
        )}

        <div className="header">
          <h1 className="title">{t('salesItem.title')}</h1>
          <div className="header-actions">
            <button
              className="btn-new"
              onClick={() => navigate('/sales-items/create')}
            >
              <CirclePlus/>
              {t('salesItem.newItem')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('salesItem.refreshItems')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('salesItem.refreshing') : t('salesItem.refreshItems')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={items}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('salesItem.noItemsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default SalesItemListPage;
